import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications, NotificationMessage } from '../contexts/NotificationsContext';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useWebSocket } from '../utils/websocket';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import AccessRequestResponseModal from '../components/AccessRequestResponseModal';
import GlobalSearchModal from '../components/GlobalSearchModal';
import { FunnelIcon, Squares2X2Icon, ChevronDownIcon, ChevronRightIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';

interface NotificationSearchFilters {
  emailSearch?: string;
  fileNameSearch?: string;
  dateFrom?: string;
  dateTo?: string;
  notificationTypes?: string[];
  status?: string[];
}

const Notifications: React.FC = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { setPageTitle } = usePageTitle();
  const { notifications, unreadCount, markAsRead, markAsReadLocal, markAllAsRead, markAllAsReadLocal } = useNotifications();
  const { user } = useAuth();
  const { preferences, loading: preferencesLoading } = useUserPreferences();
  
  // Track view mode to redirect when switching to admin mode
  const [viewMode, setViewMode] = useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });
  
  // Listen for view mode changes and redirect if needed
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.type !== 'view-mode-changed') {
        return;
      }
      const newMode = event.detail?.viewMode;
      if (newMode && newMode !== viewMode) {
        setViewMode(newMode as 'user' | 'admin' | 'sysadmin');
        
        // If on /notifications and switching to admin mode, redirect to /administration/notifications
        if (location.pathname === '/notifications' && newMode === 'admin') {
          navigate('/administration/notifications', { replace: true });
        }
        // If on /administration/notifications and switching to user mode, redirect to /notifications
        else if (location.pathname === '/administration/notifications' && newMode === 'user') {
          navigate('/notifications', { replace: true });
        }
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, [viewMode, location.pathname, navigate]);
  
  const [filter, setFilter] = useState<'all' | 'unread'>(() => {
    const saved = localStorage.getItem('notifications_activeTab');
    return saved === 'unread' ? 'unread' : 'all'; // Default to 'all'
  });
  const [searchFilters, setSearchFilters] = useState<NotificationSearchFilters>({});
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [groupByAsset, setGroupByAsset] = useState(false);
  const [localNotificationTypes, setLocalNotificationTypes] = useState<string[]>([]);
  const [localNotificationStatus, setLocalNotificationStatus] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState<'type' | 'status' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = React.useRef<HTMLDivElement>(null);

  // Read URL params from global search and apply to filters
  const initialFilters = useMemo(() => {
    const emailSearchParam = searchParams.get('emailSearch');
    const fileNameParam = searchParams.get('fileName');
    const dateFromParam = searchParams.get('dateFrom');
    const dateToParam = searchParams.get('dateTo');
    const notificationTypesParam = searchParams.get('notificationTypes');
    const statusParam = searchParams.get('status');

    if (!emailSearchParam && !fileNameParam && !dateFromParam && !dateToParam && !notificationTypesParam && !statusParam) {
      return undefined;
    }

    const newFilters: NotificationSearchFilters = {};

    if (emailSearchParam) newFilters.emailSearch = emailSearchParam;
    if (fileNameParam) newFilters.fileNameSearch = fileNameParam;
    if (dateFromParam) newFilters.dateFrom = dateFromParam;
    if (dateToParam) newFilters.dateTo = dateToParam;
    if (notificationTypesParam) {
      try {
        newFilters.notificationTypes = JSON.parse(notificationTypesParam);
      } catch (e) {
        console.error('Failed to parse notificationTypes:', e);
      }
    }
    if (statusParam) {
      try {
        newFilters.status = JSON.parse(statusParam);
      } catch (e) {
        console.error('Failed to parse status:', e);
      }
    }

    return newFilters;
  }, [searchParams]);

  // Apply initial filters when they change
  useEffect(() => {
    if (initialFilters) {
      console.log('🔄 Notifications: Applying URL filters:', initialFilters);
      // Only set filters if they have actual values (not empty strings)
      const validFilters: NotificationSearchFilters = {};
      if (initialFilters.emailSearch && initialFilters.emailSearch.trim()) {
        validFilters.emailSearch = initialFilters.emailSearch.trim();
      }
      if (initialFilters.fileNameSearch && initialFilters.fileNameSearch.trim()) {
        validFilters.fileNameSearch = initialFilters.fileNameSearch.trim();
      }
      if (initialFilters.dateFrom) validFilters.dateFrom = initialFilters.dateFrom;
      if (initialFilters.dateTo) validFilters.dateTo = initialFilters.dateTo;
      if (initialFilters.notificationTypes && initialFilters.notificationTypes.length > 0) {
        validFilters.notificationTypes = initialFilters.notificationTypes;
        setLocalNotificationTypes(initialFilters.notificationTypes);
      }
      if (initialFilters.status && initialFilters.status.length > 0) {
        validFilters.status = initialFilters.status;
        setLocalNotificationStatus(initialFilters.status);
      }
      setSearchFilters(validFilters);

      // Clear URL params
      setSearchParams({});
    }
  }, [initialFilters, setSearchParams]);
  // Check if web notifications are enabled
  const webNotificationsEnabled = preferences?.web_notifications !== false;

  // Set up WebSocket connection to receive read status updates from other clients
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const { wsClient } = useWebSocket(baseUrl, '', user?.id || '', user?.device_id || '');

  // Listen for read status updates from other clients
  useEffect(() => {
    if (!wsClient) return;

    const handleReadStatusUpdate = (data: any) => {
      if (data.all_read) {
        // All notifications marked as read (from another client via WebSocket)
        markAllAsReadLocal(); // Use local-only update to avoid unnecessary API call
      } else if (data.notification_id) {
        // Single notification marked as read (from another client via WebSocket)
        markAsReadLocal(data.notification_id); // Use local-only update to avoid unnecessary API call
      }
    };

    // Register handler for read status updates
    wsClient.onMessage('notification_read_status', handleReadStatusUpdate);

    // Cleanup is handled by WebSocketClient's messageHandlers Map
  }, [wsClient, markAsReadLocal, markAllAsReadLocal]);

  // Set page title
  useEffect(() => {
    setPageTitle(t('title'), t('subtitle'));
  }, [setPageTitle, t]);

  // Reset search filters when navigating away from /notifications page
  useEffect(() => {
    const isNotificationsPage = location.pathname === '/notifications';
    if (!isNotificationsPage) {
      // Clear filters when leaving the page
      setSearchFilters({});
    }
  }, [location.pathname]);

  // Persist tab selection
  useEffect(() => {
    localStorage.setItem('notifications_activeTab', filter);
  }, [filter]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
    };

    if (sortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortDropdownOpen]);

  // Reset filters function
  const handleResetFilters = () => {
    setSearchFilters({});
    setLocalNotificationTypes([]);
    setLocalNotificationStatus([]);
    // Clear URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('emailSearch');
    newSearchParams.delete('fileName');
    newSearchParams.delete('dateFrom');
    newSearchParams.delete('dateTo');
    newSearchParams.delete('notificationTypes');
    newSearchParams.delete('status');
    setSearchParams(newSearchParams);
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Helper function to format file name (remove .zip extension only for folders)
  const formatFileName = (fileName: string, assetType?: string): string => {
    // Only remove .zip extension if asset_type is explicitly 'folder'
    // Regular zip files should keep their .zip extension
    if (assetType?.toLowerCase() === 'folder' && fileName.toLowerCase().endsWith('.zip')) {
      return fileName.slice(0, -4); // Remove .zip extension
    }
    return fileName;
  };

  const getNotificationTitle = (type: string, payload?: any): string => {
    // Extract actual type from payload if available (backend sends type in payload)
    const actualType = payload?.type || type;

    switch (actualType) {
      case 'file_shared':
      case 'folder_shared':
        return t('types.assetShared');
      case 'file_access_revoked':
        return t('types.accessRevoked');
      case 'file_access_violation':
        return t('types.accessViolation');
      case 'file_access_request':
      case 'access_count_limit_request':
      case 'access_period_limit_request':
      case 'asset_permissions_request':
        return t('types.accessRequest');
      case 'access_request_responded':
        return t('types.accessRequestResponse');
      case 'access_request_canceled':
        return t('types.accessRequestCanceled');
      case 'policy_updated':
        return t('types.policyUpdated');
      case 'user_removed_from_group':
        return t('types.removedFromGroup');
      case 'system_announcement':
        return t('types.systemAnnouncement');
      case 'new_device_registered':
        return t('types.newDeviceRegistered');
      case 'device_revoked':
        return t('types.deviceRevoked');
      case 'infrastructure_provisioning':
        return payload?.status === 'ready' ? t('types.infrastructureReady') : t('types.infrastructureProvisioning');
      case 'license_alert':
        return payload?.reason === 'expiry' ? t('licenseAlert.licenseExpiringSoon') : t('licenseAlert.lowLicenseCount');
      default:
        return t('types.notification');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {t('status.pending')}
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t('status.accepted')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t('status.rejected')}
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {t('status.canceled')}
          </span>
        );
      default:
        return null;
    }
  };

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);


  // Extract all access request IDs from notifications to check their status
  const accessRequestIds = useMemo(() => {
    const ids = new Set<string>();
    notifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      if (actualType === 'file_access_request' ||
        actualType === 'access_count_limit_request' ||
        actualType === 'access_period_limit_request' ||
        actualType === 'asset_permissions_request') {
        const np = n.payload?.payload || n.payload;
        const requestId = np?.access_request_id || n.payload?.access_request_id;
        if (requestId) {
          ids.add(requestId);
        }
      }
    });
    return Array.from(ids);
  }, [notifications]);

  // Fetch access request statuses for all pending notifications
  const { data: accessRequestsStatus, isLoading: isLoadingStatuses } = useQuery({
    queryKey: ['access-requests-status', accessRequestIds],
    queryFn: async () => {
      const statusMap: Record<string, string> = {};
      await Promise.all(
        accessRequestIds.map(async (id) => {
          try {
            const request = await apiService.getAccessRequest(id);
            // Get status and ensure it's a string, normalize to lowercase
            const status = String(request.status || request.Status || 'pending').toLowerCase();
            statusMap[id] = status;
            console.log(`📊 Access request ${id} status:`, request.status, '→ normalized:', status);
          } catch (error) {
            // If request not found or error, assume pending
            console.error(`❌ Failed to fetch access request ${id}:`, error);
            statusMap[id] = 'pending';
          }
        })
      );
      return statusMap;
    },
    enabled: accessRequestIds.length > 0,
    staleTime: 0, // Don't cache - always fetch fresh data
    gcTime: 0, // Don't keep in cache after unmount (formerly cacheTime)
  });

  // Check if we have any search filters active
  const hasSearchFilters = useMemo(() => {
    return !!(
      (searchFilters.emailSearch && searchFilters.emailSearch.trim() !== '') ||
      (searchFilters.fileNameSearch && searchFilters.fileNameSearch.trim() !== '') ||
      searchFilters.dateFrom ||
      searchFilters.dateTo ||
      (searchFilters.notificationTypes && searchFilters.notificationTypes.length > 0) ||
      (searchFilters.status && searchFilters.status.length > 0) ||
      localNotificationTypes.length > 0 ||
      localNotificationStatus.length > 0
    );
  }, [searchFilters, localNotificationTypes, localNotificationStatus]);

  // Note: Backend doesn't support email/type filtering yet, so we use client-side filtering
  // We use the notifications from context (already loaded) and filter them client-side

  // Save current filters to localStorage for GlobalSearchModal to read
  useEffect(() => {
    const isNotificationsPage = location.pathname === '/notifications';
    if (!isNotificationsPage) {
      // Clear filters when navigating away
      localStorage.removeItem('notifications_currentFilters');
      return;
    }

    if (hasSearchFilters) {
      const modalFilters = {
        emailSearch: searchFilters.emailSearch,
        fileName: searchFilters.fileNameSearch, // GlobalSearchModal uses 'fileName' for notifications
        dateFrom: searchFilters.dateFrom,
        dateTo: searchFilters.dateTo,
        notificationType: searchFilters.notificationTypes && searchFilters.notificationTypes.length > 0 
          ? searchFilters.notificationTypes 
          : localNotificationTypes.length > 0 ? localNotificationTypes : undefined,
        notificationStatus: searchFilters.status && searchFilters.status.length > 0
          ? searchFilters.status
          : localNotificationStatus.length > 0 ? localNotificationStatus : undefined,
      };
      localStorage.setItem('notifications_currentFilters', JSON.stringify(modalFilters));
    } else {
      // Clear if no filters
      localStorage.removeItem('notifications_currentFilters');
    }
  }, [hasSearchFilters, searchFilters, localNotificationTypes, localNotificationStatus, location.pathname]);

  // Use context notifications (they're already loaded and up-to-date)
  // We'll filter them client-side since backend doesn't support filtering yet
  const baseNotifications = useMemo((): NotificationMessage[] => {
    return notifications;
  }, [notifications]);

  // Apply read/unread filter (client-side, as this is UI state)
  const baseFilteredNotifications = useMemo(() => {
    return filter === 'unread'
      ? baseNotifications.filter(n => !n.read)
      : baseNotifications;
  }, [baseNotifications, filter]);

  // Apply client-side filtering since backend doesn't support email/type filtering yet
  const filteredNotifications = useMemo(() => {
    console.log('🔍 Notifications: Applying filters:', searchFilters);
    console.log('🔍 Notifications: Base notifications count:', baseFilteredNotifications.length);
    let result = baseFilteredNotifications;

    // Apply email filter
      if (searchFilters.emailSearch && searchFilters.emailSearch.trim() !== '') {
        const searchEmail = searchFilters.emailSearch.toLowerCase().trim();
      const beforeCount = result.length;
      result = result.filter(n => {
        const notificationPayload = n.payload?.payload || n.payload;
        const emailFields = [
          notificationPayload?.requester_email,
          notificationPayload?.violated_by_email,
          notificationPayload?.user_email,
          notificationPayload?.shared_by_email,
          n.payload?.requester_email,
          n.payload?.violated_by_email,
          n.payload?.user_email,
          n.payload?.shared_by_email
        ];
        const matches = emailFields.some(email =>
          email && email.toLowerCase().includes(searchEmail)
        );
        return matches;
      });
      console.log('🔍 Notifications: Email filter applied:', searchEmail, 'Before:', beforeCount, 'After:', result.length);
      }

    // Apply file name filter
      if (searchFilters.fileNameSearch && searchFilters.fileNameSearch.trim() !== '') {
        const searchFileName = searchFilters.fileNameSearch.toLowerCase().trim();
      result = result.filter(n => {
        const notificationPayload = n.payload?.payload || n.payload;
        const fileName = notificationPayload?.file_name || n.payload?.file_name || '';
        return fileName.toLowerCase().includes(searchFileName);
      });
      }

    // Apply notification types filter (from searchFilters or local state)
    const activeTypes = searchFilters.notificationTypes && searchFilters.notificationTypes.length > 0 
      ? searchFilters.notificationTypes 
      : localNotificationTypes.length > 0 ? localNotificationTypes : null;
    if (activeTypes && activeTypes.length > 0) {
      const beforeCount = result.length;
      result = result.filter(n => {
        const actualType = n.payload?.type || n.type;
        
        // Check if the actual notification type matches any of the active filter types
        const directMatch = activeTypes.includes(actualType);
        
        // Handle special cases for grouped types
        const isAccessRequest = actualType === 'file_access_request' ||
          actualType === 'access_count_limit_request' ||
          actualType === 'access_period_limit_request' ||
          actualType === 'asset_permissions_request';
        const isAccessViolation = actualType === 'file_access_violation';
        
        // Check for grouped type matches
        const groupedMatch = activeTypes.some(type => {
          if (type === 'access_request') return isAccessRequest;
          if (type === 'access_violation') return isAccessViolation;
          return false;
        });
        
        return directMatch || groupedMatch;
      });
      console.log('🔍 Notifications: Type filter applied:', activeTypes, 'Before:', beforeCount, 'After:', result.length);
    }

    // Apply status filter (from searchFilters or local state)
    const activeStatus = searchFilters.status && searchFilters.status.length > 0
      ? searchFilters.status
      : localNotificationStatus.length > 0 ? localNotificationStatus : null;
    if (activeStatus && activeStatus.length > 0) {
      result = result.filter(n => {
        const notificationPayload = n.payload?.payload || n.payload;
        const status = notificationPayload?.status ||
          n.payload?.status ||
          accessRequestsStatus?.[notificationPayload?.access_request_id || n.payload?.access_request_id];

        const normalizedStatus = status ? String(status).toLowerCase() : 'pending';
        return activeStatus.includes(normalizedStatus);
      });
      }

    // Apply date range filter
      if (searchFilters.dateFrom) {
      result = result.filter(n => {
        const notificationDate = new Date(n.timestamp * 1000);
        const fromDate = new Date(searchFilters.dateFrom!);
        return notificationDate >= fromDate;
      });
      }

      if (searchFilters.dateTo) {
      result = result.filter(n => {
        const notificationDate = new Date(n.timestamp * 1000);
        const toDate = new Date(searchFilters.dateTo!);
        return notificationDate <= toDate;
      });
      }

    console.log('🔍 Notifications: Final filtered count:', result.length);
    return result;
  }, [baseFilteredNotifications, searchFilters, localNotificationTypes, localNotificationStatus, accessRequestsStatus]);

  // Get file icon helper function
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || 'file';
    const upperExtension = extension.toUpperCase();

    // Map extensions to icon file names (handles variations like docx -> doc)
    const extensionMap: Record<string, string> = {
      'txt': 'txt',
      'doc': 'doc',
      'docx': 'doc',
      'xls': 'xls',
      'xlsx': 'xls',
      'ppt': 'ppt',
      'pptx': 'ppt',
      'pdf': 'pdf',
      'csv': 'csv',
      'png': 'png',
      'jpg': 'jpg',
      'jpeg': 'jpg',
    };

    // Check if we have an icon for this extension
    const iconName = extensionMap[extension];
    if (iconName) {
      return (
        <img
          src={`/asset-icons/${iconName}.webp`}
          alt={`${extension} file`}
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      );
    }

    // Fallback to text-based icon for unsupported types
    return (
      <div className="w-6 h-6 flex-shrink-0 bg-black dark:bg-gray-800 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">{upperExtension}</span>
      </div>
    );
  };

  // Filter notifications: replace pending requests with canceled ones (file owner side only)
  // This uses filteredNotifications which already has search filters applied
  const processedNotifications = useMemo(() => {
    const result: any[] = [];
    const pendingRequestIds = new Set<string>();
    const canceledRequestIds = new Map<string, any>(); // access_request_id -> canceled notification

    // First pass: collect pending requests and canceled notifications
    filteredNotifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      const np = n.payload?.payload || n.payload;
      const requestId = np?.access_request_id || n.payload?.access_request_id;

      if (actualType === 'access_request_canceled' && requestId) {
        // This is a canceled notification - store it
        canceledRequestIds.set(requestId, n);
      } else if ((actualType === 'file_access_request' ||
        actualType === 'access_count_limit_request' ||
        actualType === 'access_period_limit_request' ||
        actualType === 'asset_permissions_request') &&
        requestId) {
        // Check if this is a pending request
        const status = np?.status || n.payload?.status;
        if (!status || status === 'pending') {
          pendingRequestIds.add(requestId);
        }
      }
    });

    // Second pass: build result, replacing pending with canceled when both exist
    filteredNotifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      const np = n.payload?.payload || n.payload;
      const requestId = np?.access_request_id || n.payload?.access_request_id;

      if (actualType === 'access_request_canceled' && requestId) {
        // If there's a pending request for this, skip the canceled notification
        // (we'll replace the pending one instead)
        if (!pendingRequestIds.has(requestId)) {
          result.push(n);
        }
      } else if ((actualType === 'file_access_request' ||
        actualType === 'access_count_limit_request' ||
        actualType === 'access_period_limit_request' ||
        actualType === 'asset_permissions_request') &&
        requestId) {
        const status = np?.status || n.payload?.status;
        if (!status || status === 'pending') {
          // Check if there's a canceled notification for this request
          if (canceledRequestIds.has(requestId)) {
            // Replace pending with canceled notification
            result.push(canceledRequestIds.get(requestId)!);
          } else {
            result.push(n);
          }
        } else {
          // Not pending, add as-is
          result.push(n);
        }
      } else {
        // Not an access request notification, add as-is
        result.push(n);
      }
    });

    return result;
  }, [filteredNotifications]);

  // Sort notifications based on sortField and sortOrder
  const sortedNotifications = useMemo(() => {
    return [...processedNotifications].sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'type') {
        const typeA = (a.payload?.type || a.type || '').toLowerCase();
        const typeB = (b.payload?.type || b.type || '').toLowerCase();
        comparison = typeA.localeCompare(typeB);
      } else if (sortField === 'status') {
        // Get status from notification payload or access request status
        const npA = a.payload?.payload || a.payload;
        const npB = b.payload?.payload || b.payload;
        const requestIdA = npA?.access_request_id || a.payload?.access_request_id;
        const requestIdB = npB?.access_request_id || b.payload?.access_request_id;
        const statusA = (npA?.status || a.payload?.status || accessRequestsStatus?.[requestIdA] || 'pending').toLowerCase();
        const statusB = (npB?.status || b.payload?.status || accessRequestsStatus?.[requestIdB] || 'pending').toLowerCase();
        comparison = statusA.localeCompare(statusB);
      } else if (sortField === 'date') {
        const dateA = a.timestamp || (a.created_at ? new Date(a.created_at).getTime() / 1000 : 0);
        const dateB = b.timestamp || (b.created_at ? new Date(b.created_at).getTime() / 1000 : 0);
        comparison = dateA - dateB;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [processedNotifications, sortField, sortOrder, accessRequestsStatus]);

  // Group notifications by asset (file_name) if groupByAsset is enabled
  const groupedNotifications = useMemo(() => {
    if (!groupByAsset) return null;
    
    const groups = new Map<string, NotificationMessage[]>();
    sortedNotifications.forEach(notification => {
      const notificationPayload = notification.payload?.payload || notification.payload;
      const fileName = notificationPayload?.file_name || notification.payload?.file_name || 'Unknown';
      const fileId = notificationPayload?.file_id || notification.payload?.file_id || fileName;
      
      const key = fileId || fileName;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(notification);
    });
    
    return groups;
  }, [sortedNotifications, groupByAsset]);

  // Toggle group expansion
  const toggleGroup = (fileId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Pagination logic
  const paginatedNotifications = useMemo(() => {
    if (groupByAsset && groupedNotifications) {
      const groupsArray = Array.from(groupedNotifications.entries());
      const totalPages = Math.ceil(groupsArray.length / itemsPerPage);
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return {
        items: groupsArray.slice(startIndex, endIndex),
        totalItems: groupsArray.length,
        totalPages,
        startIndex,
        endIndex,
      };
    } else {
      const totalPages = Math.ceil(sortedNotifications.length / itemsPerPage);
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return {
        items: sortedNotifications.slice(startIndex, endIndex),
        totalItems: sortedNotifications.length,
        totalPages,
        startIndex,
        endIndex,
      };
    }
  }, [sortedNotifications, groupByAsset, groupedNotifications, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filters or grouping changes
  useEffect(() => {
    setCurrentPage(1);
  }, [groupByAsset, searchFilters, filter]);

  const handleNotificationClick = (notification: any) => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // For access requests and license requests with non-pending status, open the modal
    const actualType = notification.payload?.type || notification.type;
    const notificationPayload = notification.payload?.payload || notification.payload;

    // Check if it's an access request with non-pending status
    const isAccessRequest = actualType === 'file_access_request' ||
      actualType === 'access_count_limit_request' ||
      actualType === 'access_period_limit_request' ||
      actualType === 'asset_permissions_request';
    
    if (isAccessRequest) {
      const requestId = notificationPayload?.access_request_id || notification.payload?.access_request_id;
      if (requestId) {
        const payloadStatus = notificationPayload?.status || notification.payload?.status;
        const requestStatus = accessRequestsStatus?.[requestId] as string | undefined;
        const finalStatus = payloadStatus || requestStatus;
        
        if (finalStatus && finalStatus !== 'pending') {
          setSelectedRequestId(requestId);
          setSelectedFileId(notificationPayload?.file_id || notification.payload?.file_id);
          setShowResponseModal(true);
          return;
        }
      }
    }


    // Handle infrastructure provisioning notifications - navigate to Infrastructure page
    if (actualType === 'infrastructure_provisioning' && notificationPayload?.action_url) {
      navigate(notificationPayload.action_url);
      return;
    }
  };

  const renderNotification = (notification: any, isGrouped: boolean = false) => {
    const { type, payload, id, read, timestamp } = notification;
    // Extract actual type from payload if available (backend sends type in payload)
    // Payload structure: { type, payload: { access_request_id, file_id, ... }, ... }
    const actualType = payload?.type || type;
    const notificationPayload = payload?.payload || payload; // Get nested payload if exists
    const title = getNotificationTitle(actualType, payload);

    return (
      <div
        key={id || `notification-${Date.now()}-${Math.random()}`}
        onClick={() => handleNotificationClick(notification)}
        className={`${isGrouped ? 'p-2' : 'p-4'} border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer ${!read ? 'bg-blue-50' : ''
          }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {actualType === 'infrastructure_provisioning' ? (
                // Show spinner for provisioning, checkmark for ready
                notificationPayload?.status === 'ready' ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : notificationPayload?.status === 'failed' ? (
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )
              ) : !read ? (
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              ) : null}
              <h4 className="text-sm font-medium text-gray-900">{title}</h4>
            </div>

            {(type === 'file_shared' || type === 'folder_shared') && (
              <div>
                <p className="text-sm text-gray-600">
                  <strong>{formatFileName(
                    payload?.file_name || notificationPayload?.file_name || '',
                    payload?.asset_type || notificationPayload?.asset_type
                  )}</strong> has been shared with you by{' '}
                  <strong>{payload?.shared_by_email || notificationPayload?.shared_by_email || payload?.shared_by_name || notificationPayload?.shared_by_name || 'Someone'}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.timestamp || notificationPayload?.timestamp || timestamp)}
                </p>
              </div>
            )}

            {type === 'file_access_violation' && (
              <div className="flex items-start gap-2">
                {!isGrouped && (payload?.file_name || payload?.payload?.file_name) && (
                  <div className="mt-0.5">
                    {getFileIcon(payload?.file_name || payload?.payload?.file_name)}
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    <strong>{payload?.violated_by_email || payload?.payload?.violated_by_email || payload?.user_email}</strong> attempted to access your file{' '}
                    <strong>{payload?.file_name || payload?.payload?.file_name}</strong> but was denied
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {payload?.violation_type || payload?.payload?.violation_type}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                  </p>
                </div>
              </div>
            )}

            {/* Access Request - File Owner View (pending requests) */}
            {(actualType === 'file_access_request' ||
              actualType === 'access_count_limit_request' ||
              actualType === 'access_period_limit_request' ||
              actualType === 'asset_permissions_request') && (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 flex items-start gap-2">
                    {!isGrouped && (notificationPayload?.file_name || payload?.file_name) && (
                      <div className="mt-0.5">
                        {getFileIcon(notificationPayload?.file_name || payload?.file_name)}
                      </div>
                    )}
                    <div className="flex-1">
                      {/* Show detailed message (from notification.message) - this is more detailed */}
                      {notification.message ? (
                        <p className="text-sm text-gray-600">
                          {notification.message}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600">
                          <strong>{notificationPayload?.requester_email || payload?.requester_email || payload?.user_email || payload?.requested_by_email}</strong> is requesting access to{' '}
                          <strong>{notificationPayload?.file_name || payload?.file_name}</strong>
                        </p>
                      )}
                      {(notificationPayload?.request_reason || notificationPayload?.reason || notificationPayload?.requester_message || payload?.request_reason || payload?.reason || payload?.requester_message) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Reason: {notificationPayload?.request_reason || notificationPayload?.reason || notificationPayload?.requester_message || payload?.request_reason || payload?.reason || payload?.requester_message}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(payload?.timestamp || timestamp)}
                      </p>
                    </div>
                  </div>
                  {/* Show status circle or Respond button at the end of the row */}
                  {(() => {
                    const requestId = notificationPayload?.access_request_id || payload?.access_request_id;
                    if (!requestId) return null;

                    // Check status from notification payload first
                    const payloadStatus = notificationPayload?.status || payload?.status;
                    // Check status from fetched access request data
                    const requestStatus = accessRequestsStatus?.[requestId] as string | undefined;
                    const finalStatus = payloadStatus || requestStatus;

                    // If we're still loading and don't have a status yet, don't show anything to avoid flash
                    if (isLoadingStatuses && !payloadStatus && !requestStatus) {
                      return null;
                    }

                    // If status exists and is not pending, show status circle at the end
                    if (finalStatus && finalStatus !== 'pending') {
                      const statusColor = finalStatus === 'accepted' || finalStatus === 'approved' ? 'bg-green-500' :
                        finalStatus === 'rejected' || finalStatus === 'denied' ? 'bg-red-500' :
                        'bg-gray-500';
                      return (
                        <div className={`w-2.5 h-2.5 rounded-full ${statusColor} flex-shrink-0`}></div>
                      );
                    }

                    // Otherwise show Respond button (pending or no status found)
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequestId(requestId);
                          setSelectedFileId(notificationPayload?.file_id || payload?.file_id);
                          setShowResponseModal(true);
                        }}
                        className="text-xs border-2 border-black text-black bg-white px-2 py-1 rounded hover:bg-gray-50 whitespace-nowrap"
                      >
                        Respond
                      </button>
                    );
                  })()}
                </div>
              )}

            {/* Access Request Response - Recipient View (when their request was accepted/rejected) */}
            {actualType === 'access_request_responded' && (
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {/* Show notification message (avoid duplication) */}
                  {notification.message ? (
                    <p className="text-sm text-gray-600">
                      {notification.message}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Your access request for <strong>{notificationPayload?.file_name || payload?.file_name}</strong> has been{' '}
                      <strong>{notificationPayload?.status || payload?.status || 'responded to'}</strong>
                    </p>
                  )}
                  {(notificationPayload?.response_message || payload?.response_message) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Message: {notificationPayload?.response_message || payload?.response_message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                  </p>
                </div>
                {/* Show status badge on right side only (for recipients) */}
                {(notificationPayload?.status || payload?.status) && (
                  <div>
                    {getStatusBadge(notificationPayload?.status || payload?.status)}
                  </div>
                )}
              </div>
            )}

            {/* Access Request Canceled - File Owner View */}
            {actualType === 'access_request_canceled' && (
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {notification.message ? (
                    <p className="text-sm text-gray-600">
                      {notification.message}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      <strong>{notificationPayload?.requester_email || payload?.requester_email || payload?.user_email}</strong> has canceled their access request for{' '}
                      <strong>{notificationPayload?.file_name || payload?.file_name}</strong>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                  </p>
                </div>
                {/* Show status badge on right side only */}
                <div>
                  {getStatusBadge('canceled')}
                </div>
              </div>
            )}

            {type === 'new_device_registered' && (
              <div>
                <p className="text-sm text-gray-600">
                  New device <strong>{payload?.payload?.device_name || payload?.device_name}</strong> registered for user{' '}
                  <strong>{payload?.payload?.user_email || payload?.user_email}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Device ID: {payload?.payload?.device_id || payload?.device_id}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.payload?.timestamp || payload?.timestamp || timestamp)}
                </p>
              </div>
            )}

            {type === 'device_revoked' && (
              <div>
                <p className="text-sm text-gray-600">
                  Device <strong>{payload?.payload?.device_name || payload?.device_name}</strong> has been revoked
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.payload?.timestamp || payload?.timestamp || timestamp)}
                </p>
              </div>
            )}

            {/* User Pending Enrollment - Company Admin View */}
            {actualType === 'user_pending_enrollment' && (
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {notification.message ? (
                    <p className="text-sm text-gray-600">{notification.message}</p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      <strong>{notificationPayload?.user_email || payload?.user_email}</strong> registered but is inactive. Request new licenses to activate this user.
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                  </p>
                </div>
              </div>
            )}

            {actualType === 'infrastructure_provisioning' && (
              <div>
                <p className="text-sm text-gray-600">
                  {notification.message || notificationPayload?.message || 'Infrastructure provisioning in progress...'}
                </p>
                {notificationPayload?.url && (
                  <p className="text-xs text-gray-500 mt-1">
                    URL: {notificationPayload.url}
                  </p>
                )}
                {notificationPayload?.action_url && (
                  <p className="text-xs text-blue-600 mt-1">
                    {t('clickToViewInfrastructure')}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                </p>
              </div>
            )}

            {actualType === 'license_alert' && (
              <div>
                <p className="text-sm text-gray-600">
                  <strong>{notificationPayload?.company_name || payload?.company_name}</strong>
                </p>
                {(notificationPayload?.available_licenses !== undefined || payload?.available_licenses !== undefined) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('licensesLeft', { available: notificationPayload?.available_licenses ?? payload?.available_licenses, total: notificationPayload?.total_licenses ?? payload?.total_licenses })}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                </p>
              </div>
            )}

            {!['file_shared', 'file_access_violation', 'file_access_request', 'access_count_limit_request', 'access_period_limit_request', 'asset_permissions_request', 'access_request_responded', 'access_request_canceled', 'new_device_registered', 'device_revoked', 'user_pending_enrollment', 'infrastructure_provisioning', 'license_alert'].includes(actualType) && (
              <div>
                <p className="text-sm text-gray-600">{notification.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(payload?.timestamp || payload?.created_at || timestamp)}
                </p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {/* Manage File button for file_access_violation only - Hide when grouped (shown in accordion header instead) */}
            {!isGrouped && actualType === 'file_access_violation' &&
              (payload?.file_id || payload?.payload?.file_id) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const fileId = payload?.file_id || payload?.payload?.file_id;
                    navigate(`/assets/${fileId}/manage`);
                    window.location.href = `/assets/${fileId}/manage`;
                  }}
                  className="text-xs border-2 border-black text-black bg-white px-2 py-1 rounded hover:bg-gray-50 whitespace-nowrap"
                >
                  {t('manageFile')}
                </button>
              )}
            {/* Details button for license_alert - goes to License alerts page (shown in both normal and grouped view) */}
            {actualType === 'license_alert' &&
              (notificationPayload?.company_id || payload?.company_id) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const companyId = notificationPayload?.company_id || payload?.company_id;
                    navigate(`/administration/licenses?company_id=${encodeURIComponent(companyId)}`);
                  }}
                  className="text-xs border-2 border-black text-black bg-white px-2 py-1 rounded hover:bg-gray-50 whitespace-nowrap dark:border-white dark:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  {t('details')}
                </button>
              )}
          </div>
        </div>
      </div>
    );
  };

  // Show message if web notifications are disabled
  if (!preferencesLoading && !webNotificationsEnabled) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t('webNotificationsDisabled')}
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>
                  {t('webNotificationsDisabledDesc')}{' '}
                  <button
                    onClick={() => navigate('/settings?tab=notifications')}
                    className="underline font-medium hover:text-yellow-900 dark:hover:text-yellow-100"
                  >
                    {t('settingsNotifications')}
                  </button>
                  {' '}{t('toViewAndManage')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Filter and Actions */}
      <div className="flex flex-col gap-4">
        {/* Top row: All/Unread toggle, Type, Status, Group by Asset, Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side: All/Unread toggle and Mark All as Read */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
              className="px-4 py-2 rounded-md text-sm font-medium border-2 transition-colors bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {filter === 'all' 
                ? hasSearchFilters 
                  ? t('filters.allWithCount', { count: sortedNotifications.length }) 
                  : t('filters.allWithCount', { count: notifications.length })
                : t('filters.unreadWithCount', { count: unreadCount })}
            </button>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 sm:px-4 py-2 border-2 border-black text-black bg-white rounded-md text-sm font-medium hover:bg-gray-50 dark:border-white dark:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                {t('markAllAsRead')}
              </button>
            )}
          </div>

          {/* Middle: Sort dropdown */}
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400">{t('sortBy')}</span>
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 pr-6 text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px] h-[36px] flex items-center justify-between"
              >
                <span className="truncate">
                  {sortField === 'type' ? t('sortType') :
                   sortField === 'status' ? t('sortStatus') :
                   t('sortDate')}
                </span>
                <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${sortDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {sortDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {[
                    { value: 'date', label: t('sortDate') },
                    { value: 'type', label: t('sortType') },
                    { value: 'status', label: t('sortStatus') },
                  ].filter(option => option.value !== sortField).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSortField(option.value as 'type' | 'status' | 'date');
                        setSortOrder('asc');
                        setSortDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              }}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title={sortOrder === 'asc' ? t('sortDescending') : t('sortAscending')}
            >
              <ArrowsUpDownIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Right side: Group by Asset and Filters */}
          <div className="flex items-center gap-2">
            {/* Group by Asset Button */}
            <button
              onClick={() => {
                setGroupByAsset(!groupByAsset);
                setCurrentPage(1);
                setExpandedGroups(new Set());
              }}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                groupByAsset
                  ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                }`}
            >
              <Squares2X2Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t('groupByAsset')}</span>
            </button>

            {/* Reset Filters Button - Show when filters are active */}
            {hasSearchFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors dark:bg-gray-800 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                title={t('resetFilters')}
              >
                <span className="hidden sm:inline">{t('resetFilters')}</span>
                <span className="sm:hidden">{t('reset')}</span>
              </button>
            )}

            {/* Filter Button - Last */}
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
              title={t('filtersLabel')}
            >
              <FunnelIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{t('filtersLabel')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {paginatedNotifications.totalItems === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'unread' ? t('empty.noUnread') : t('empty.noNotifications')}
              </h3>
              <p className="text-gray-600">
              {hasSearchFilters
                ? t('empty.noMatch')
                : filter === 'unread'
                  ? t('empty.allCaughtUp')
                  : t('empty.whenArrive')
                }
              </p>
            </div>
        ) : groupByAsset && groupedNotifications ? (
          /* Grouped View - Accordion (only for 2+ items) or Regular Row (for 1 item) */
          <div className="space-y-0">
            {paginatedNotifications.items.map(([fileId, groupNotifications]) => {
              const isExpanded = expandedGroups.has(fileId);
              const firstNotification = groupNotifications[0];
              const notificationPayload = firstNotification.payload?.payload || firstNotification.payload;
              const fileName = notificationPayload?.file_name || notificationPayload?.file_name || 'Unknown';
              const fileIdFromPayload = notificationPayload?.file_id || notificationPayload?.file_id;
              const recordCount = groupNotifications.length;
              
              // If only 1 notification, render as regular row (not accordion)
              if (recordCount === 1) {
                return (
                  <div
                    key={fileId}
                    className="border-l-4 border-l-transparent bg-white border-b border-gray-200"
                  >
                    {renderNotification(firstNotification, false)}
                  </div>
                );
              }
              
              // If 2+ notifications, render as accordion
              return (
                <div
                  key={fileId}
                  className="border-l-4 border-l-transparent bg-white border-b border-gray-200"
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleGroup(fileId)}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* File Name */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{fileName}</div>
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {t('notificationsCount', { count: recordCount })}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Only show Manage File button if not file_shared type */}
                      {fileIdFromPayload && (() => {
                        const actualType = firstNotification.payload?.type || firstNotification.type;
                        return actualType !== 'file_shared';
                      })() && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/assets/${fileIdFromPayload}/manage`);
                          }}
                          className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                        >
                          Manage File
                        </button>
                      )}
                      {isExpanded ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="bg-gray-50">
                      {groupNotifications.map((notification: NotificationMessage) => (
                        <div
                          key={notification.id}
                          className="border-l-4 border-l-transparent bg-white hover:bg-gray-50 transition-colors px-4 py-2 border-b border-gray-200"
                        >
                          {renderNotification(notification, true)} {/* Pass true to indicate grouped view */}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Regular View */
          <div>
            {paginatedNotifications.items.map((notification) => renderNotification(notification, false))}
          </div>
        )}

        {/* Pagination */}
        {paginatedNotifications.totalItems > itemsPerPage && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= paginatedNotifications.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium text-gray-900">{paginatedNotifications.startIndex + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium text-gray-900">
                    {Math.min(paginatedNotifications.endIndex, paginatedNotifications.totalItems)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium text-gray-900">{paginatedNotifications.totalItems}</span>
                  {' '}{groupByAsset ? 'groups' : 'results'}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="items-per-page" className="text-sm text-gray-700">
                    Show:
                  </label>
                  <select
                    id="items-per-page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= paginatedNotifications.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Access Request Response Modal */}
      {
        showResponseModal && selectedRequestId && (
          <AccessRequestResponseModal
            isOpen={showResponseModal}
            onClose={() => {
              setShowResponseModal(false);
              setSelectedRequestId(null);
              setSelectedFileId(null);
            }}
            requestId={selectedRequestId}
            fileId={selectedFileId || undefined}
            notificationId={notifications.find(n => {
              const np = n.payload?.payload || n.payload;
              return (np?.access_request_id || n.payload?.access_request_id) === selectedRequestId;
            })?.id}
            onResponded={() => {
              // Invalidate and refetch access request statuses immediately after responding
              queryClient.invalidateQueries({ queryKey: ['access-requests-status'] });
              // Refresh notifications after responding
              // The notification context will automatically update via WebSocket
            }}
          />
        )
      }


      {/* Global Search Modal */}
      {isSearchModalOpen && (
        <GlobalSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          initialTab="notifications"
          singleTabMode={true}
          initialFilterValues={(() => {
            // Only read from localStorage if we're on the notifications page
            if (location.pathname === '/notifications') {
              try {
                const saved = localStorage.getItem('notifications_currentFilters');
                if (saved) return JSON.parse(saved);
              } catch (e) {
                console.error('Failed to parse saved filters:', e);
              }
            }
            // Fallback to current filters state
            const hasActiveFilters = hasSearchFilters;
            if (!hasActiveFilters) return undefined;
            return {
              emailSearch: searchFilters.emailSearch,
              fileName: searchFilters.fileNameSearch,
              dateFrom: searchFilters.dateFrom,
              dateTo: searchFilters.dateTo,
              notificationType: searchFilters.notificationTypes,
              notificationStatus: searchFilters.status,
            };
          })()}
        />
      )}
    </div >
  );
};

export default Notifications;